"use client";

import React, { useState, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import { env } from "@/lib/environment";

interface GalleryItem {
  id: string;
  thumbImage: string;
  fullImage: string;
  logoImage: string;
  modalLogoImage?: string;
  logoImageDark?: string;
  logoAlt: string;
  description?: string;
  caseStudyLink?: string;
  alt: string;
}

const cdnBaseUrl = env.NEXT_PUBLIC_CDN_URL;

const galleryItems: GalleryItem[] = [
  {
    id: "amazon",
    thumbImage: `${cdnBaseUrl}/images/gallery/amazon/amazon-easy.w2JFw5h7veU1.webp`,
    fullImage: `${cdnBaseUrl}/images/gallery/amazon/amazon-easy.w2JFw5h7veU1.webp`,
    logoImage: `${cdnBaseUrl}/images/gallery/amazon/amazon-logo.4v-VTP8yobfO.png`,
    modalLogoImage: `${cdnBaseUrl}/images/gallery/amazon/amazon-logo._4P3TMr-4reT.jpg`,
    logoAlt: "Amazon logo",
    description:
      "Pickcel's software enables the Amazon retail kiosks to launch dynamic digital signage campaigns across 25+ malls.",
    caseStudyLink: "/case-studies/amazon",
    alt: "Pickcel powered Digital signage displays at an amazon stall in a mall",
  },
  {
    id: "saya-homes",
    thumbImage: `${cdnBaseUrl}/images/gallery/saya-homes/saya-homes-thumb.8qd4gZF_m2m7.webp`,
    fullImage: `${cdnBaseUrl}/images/gallery/saya-homes/saya-homes.JFd091ptigx_.webp`,
    logoImage: `${cdnBaseUrl}/images/gallery/saya-homes/saya-homes-logo.rsk3jh6nH9tZ.png`,
    modalLogoImage: `${cdnBaseUrl}/images/gallery/saya-homes/saya-homes-logo.LpMapuR7FRTJ.jpg`,
    logoAlt: "Saya Homes logo",
    description:
      "This popular real-estate business uses Pickcel services to publish property ads on screens located in high-traffic areas.",
    alt: "Pickcel powered Digital signage display at Saya office lobby",
  },
  {
    id: "mad-co",
    thumbImage: `${cdnBaseUrl}/images/gallery/mad-co/mad-co-thumb.vQ8oaurjp498.webp`,
    fullImage: `${cdnBaseUrl}/images/gallery/mad-co/mad-co.DjkO2BgBSxE6.webp`,
    logoImage: `${cdnBaseUrl}/images/gallery/mad-co/mad-co-logo.66bKZ9CxI8fX.png`,
    modalLogoImage: `${cdnBaseUrl}/images/gallery/mad-co/mad-co-logo.CDEap55eonFk.jpg`,
    logoAlt: "Mad & Co logo",
    description:
      "Mad & Co. is connecting with boba-tea-lovers across the country through their hip & groovy marketing campaigns on digital signage.",
    caseStudyLink: "/case-studies/mad-and-co-qsr-digital-signage",
    alt: "Pickcel powered Digital signage display at Mad and Co cafe",
  },
  {
    id: "namdharis",
    thumbImage: `${cdnBaseUrl}/images/gallery/namdharis/namdharis-fresh-thumb.agEIy5___xLY.webp`,
    fullImage: `${cdnBaseUrl}/images/gallery/namdharis/namdharis-fresh.OUCfjbtTU4Ye.webp`,
    logoImage: `${cdnBaseUrl}/images/gallery/namdharis/namdharis-fresh-logo.NNj_cRKoZ_Pl.png`,
    modalLogoImage: `${cdnBaseUrl}/images/gallery/namdharis/namdharis-fresh-logo.S4ggI0xkivKu.jpg`,
    logoAlt: "Namdharis Fresh logo",
    description:
      "The Namdhari staff uses the Pickcel display CMS to schedule the playback of various product ads & brand messages.",
    alt: "Pickcel powered Digital signage display at Namdharis store",
  },
  {
    id: "decathlon",
    thumbImage: `${cdnBaseUrl}/images/gallery/decathlon/decathlon-thumb.OF4_m5UMaD8S.webp`,
    fullImage: `${cdnBaseUrl}/images/gallery/decathlon/decathlon.v9nKlsVSDBzr.webp`,
    logoImage: `${cdnBaseUrl}/images/gallery/decathlon/decathlon-logo.DXDTcVLfSn7y.png`,
    modalLogoImage: `${cdnBaseUrl}/images/gallery/decathlon/decathlon-logo.hlVmmTeXmNUq.jpg`,
    logoAlt: "Decathlon logo",
    description:
      "The French sporting brand uses the Pickcel digital display software to publish in-store promotions, & run product tutorial videos.",
    caseStudyLink: "/case-studies/decathlon-sports-retail-digital-signage",
    alt: "Pickcel powered Digital signage display at Decathlon store",
  },
  {
    id: "mdp",
    thumbImage: `${cdnBaseUrl}/images/gallery/mdp/mdp-thumb.kU_lokYgUWEx.webp`,
    fullImage: `${cdnBaseUrl}/images/gallery/mdp/mdp.hAKcRgSdTZUL.webp`,
    logoImage: `${cdnBaseUrl}/images/gallery/mdp/mdp-logo.Ejub0vByom3y.jpg`,
    modalLogoImage: `${cdnBaseUrl}/images/gallery/mdp/mdp-logo.Ejub0vByom3y.jpg`,
    logoAlt: "MDP logo",
    description:
      "Pickcel's digital signage solution supports the retail brand in increasing the visibility of their best-selling merchandise.",
    alt: "Pickcel powered Digital signage display at MDP office",
  },
  {
    id: "funstation",
    thumbImage: `${cdnBaseUrl}/images/gallery/funstation/funstation-thumb.YS0_aFK6Pc1U.webp`,
    fullImage: `${cdnBaseUrl}/images/gallery/funstation/funstation.XQthu_CG1Cjk.webp`,
    logoImage: `${cdnBaseUrl}/images/gallery/funstation/funstation-logo.vJWlEjLKVw9F.png`,
    modalLogoImage: `${cdnBaseUrl}/images/gallery/funstation/funstation-logo.vJWlEjLKVw9F.png`,
    logoAlt: "Funstation logo",
    description:
      "The hanging displays at Scotland's Funstation engage customers with game slots, game scores & food menus at the counters.",
    caseStudyLink: "/case-studies/fun-station-arcade-digital-signage",
    alt: "Pickcel powered Digital signage display at Funstation store",
  },
  {
    id: "weyou",
    thumbImage: `${cdnBaseUrl}/images/gallery/weyou/weyou-thumb.PYisnPUcNVvG.webp`,
    fullImage: `${cdnBaseUrl}/images/gallery/weyou/weyou.Jc0Tm6hRMUN3.webp`,
    logoImage: `${cdnBaseUrl}/images/gallery/weyou/we-you-logo.Maja0AB4LbIQ.png`,
    modalLogoImage: `${cdnBaseUrl}/images/gallery/weyou/weyou-logo.HsUFbcVFdJEL.jpg`,
    logoAlt: "WeYou logo",
    description:
      "WeYou uses Pickcel solutions to power outdoor digital signs and popularize their membership plans among a growing community of fitness-junkies.",
    alt: "Pickcel powered Digital signage display at WeYou store",
  },
  {
    id: "paralenz",
    thumbImage: `${cdnBaseUrl}/images/gallery/paralenz/paralenz-thumb.gJjDHF_Slky8.webp`,
    fullImage: `${cdnBaseUrl}/images/gallery/paralenz/paralenz.2vVg8qKfj2Is.webp`,
    logoImage: `${cdnBaseUrl}/images/gallery/paralenz/paralenz-logo.YBzD9kUJNNXt.png`,
    modalLogoImage: `${cdnBaseUrl}/images/gallery/paralenz/paralenz-logo.d7LV62ewX7-L.jpg`,
    logoAlt: "Paralenz logo",
    description:
      "This Denmark-based brand uses the Pickcel CMS to manage a global network of product information displays at 100+ franchise stores.",
    caseStudyLink: "/case-studies/paralenz-retail-digital-signage",
    alt: "Pickcel powered Digital signage display at Paralenz store",
  },
  {
    id: "vedanta",
    thumbImage: `${cdnBaseUrl}/images/gallery/vedanta/vedanta-thumb.CyVdfP_JRSQ8.webp`,
    fullImage: `${cdnBaseUrl}/images/gallery/vedanta/vedanta.h5jmtTBqZqcB.webp`,
    logoImage: `${cdnBaseUrl}/images/gallery/vedanta/vedanta-logo.hC1mJNX_Jj77.png`,
    modalLogoImage: `${cdnBaseUrl}/images/gallery/vedanta/vedanta-logo.hC1mJNX_Jj77.png`,
    logoAlt: "Vedanta logo",
    alt: "Pickcel powered Digital signage display at Vedanta classroom",
  },
  {
    id: "lulu",
    thumbImage: `${cdnBaseUrl}/images/gallery/lulu/lulu-thumb.iNlnLlmLQZbv.webp`,
    fullImage: `${cdnBaseUrl}/images/gallery/lulu/lulu.IlatMnrtOX2j.webp`,
    logoImage: `${cdnBaseUrl}/images/gallery/lulu/lulu-logo.npmWgN_0Nvrx.png`,
    modalLogoImage: `${cdnBaseUrl}/images/gallery/lulu/lulu-logo.npmWgN_0Nvrx.png`,
    logoAlt: "Lulu logo",
    alt: "Pickcel powered Digital signage display at Lulu store",
  },
  {
    id: "faden",
    thumbImage: `${cdnBaseUrl}/images/gallery/faden/faden-thumb.7VP9g3QMJBJf.webp`,
    fullImage: `${cdnBaseUrl}/images/gallery/faden/faden-media.zLgJg_2h8A0S.webp`,
    logoImage: `${cdnBaseUrl}/images/gallery/faden/faden-logo.22zb3BXcLre_.png`,
    modalLogoImage: `${cdnBaseUrl}/images/gallery/faden/faden-logo-dark.RZi26I47aQoT.png`,
    logoAlt: "Faden Media logo",
    alt: "Pickcel powered Digital signage display at Faden store",
  },
  {
    id: "second-cup",
    thumbImage: `${cdnBaseUrl}/images/gallery/second-cup/second-cup-thumb.D7onKSywbvo_.webp`,
    fullImage: `${cdnBaseUrl}/images/gallery/second-cup/second-cup.dZ_VJ4kZyOzG.webp`,
    logoImage: `${cdnBaseUrl}/images/gallery/second-cup/second-cup-logo.Yy_2t5RX5dZ4.png`,
    modalLogoImage: `${cdnBaseUrl}/images/gallery/second-cup/second-cup-logo.Yy_2t5RX5dZ4.png`,
    logoAlt: "Second Cup logo",
    alt: "Pickcel powered Digital signage display at Second Cup store",
  },
  {
    id: "chavadi",
    thumbImage: `${cdnBaseUrl}/images/gallery/chavadi/chavadi-thumb.FHc0kahKYnmf.webp`,
    fullImage: `${cdnBaseUrl}/images/gallery/chavadi/chavadi.qVp9ARdwmXuK.webp`,
    logoImage: `${cdnBaseUrl}/images/gallery/chavadi/chavadi-logo.AhrT4sZE9knK.png`,
    modalLogoImage: `${cdnBaseUrl}/images/gallery/chavadi/chavadi-logo.AhrT4sZE9knK.png`,
    logoAlt: "Chavadi logo",
    alt: "Pickcel powered Digital signage display at Chavadi store",
  },
  {
    id: "cult-fit",
    thumbImage: `${cdnBaseUrl}/images/gallery/cult-fit/cult-fit-thumb.CEIbF8VRKBJ6.webp`,
    fullImage: `${cdnBaseUrl}/images/gallery/cult-fit/cult-fit.ihtC6IQMDsFA.webp`,
    logoImage: `${cdnBaseUrl}/images/gallery/cult-fit/cult-fit-logo.JGZPnrFbAm7S.png`,
    modalLogoImage: `${cdnBaseUrl}/images/gallery/cult-fit/cult-fit-logo-dark._mv9-lBYi4q6.png`,
    logoAlt: "Cult.fit logo",
    alt: "Pickcel powered Digital signage display at Cult.fit store",
  },
  {
    id: "burjeel",
    thumbImage: `${cdnBaseUrl}/images/gallery/burjeel/burjeel-thumb.2ZYjyVqml0g1.webp`,
    fullImage: `${cdnBaseUrl}/images/gallery/burjeel/burjeel.0TNbkl6ejNCd.webp`,
    logoImage: `${cdnBaseUrl}/images/gallery/burjeel/burjeel-logo.iYuhYXMwx9Ch.png`,
    modalLogoImage: `${cdnBaseUrl}/images/gallery/burjeel/burjeel-logo.iYuhYXMwx9Ch.png`,
    logoAlt: "Burjeel logo",
    alt: "Pickcel powered Digital signage display at Burjeel store",
  },
  {
    id: "enjoy",
    thumbImage: `${cdnBaseUrl}/images/gallery/enjoy/enjoy-thumb.FKnozlO0RiYP.webp`,
    fullImage: `${cdnBaseUrl}/images/gallery/enjoy/enjoy.yYCuW3m8Zw30.webp`,
    logoImage: `${cdnBaseUrl}/images/gallery/enjoy/enjoy-logo.dG8WpyseDsnW.png`,
    modalLogoImage: `${cdnBaseUrl}/images/gallery/enjoy/enjoy-logo.dG8WpyseDsnW.png`,
    logoAlt: "Enjoy logo",
    alt: "Pickcel powered Digital signage display at Enjoy store",
  },
];

function DigitalSignageGalleryGrid() {
  const [selectedItem, setSelectedItem] = useState<GalleryItem | null>(null);

  const openModal = (item: GalleryItem) => {
    setSelectedItem(item);
  };

  const closeModal = () => {
    setSelectedItem(null);
  };

  const handleKeyPress = (event: React.KeyboardEvent, item: GalleryItem) => {
    if (
      event.key === "Enter" ||
      event.code === "Space" ||
      event.key === " " ||
      event.key === "Spacebar"
    ) {
      event.preventDefault();
      openModal(item);
    }
  };

  // Handle Escape key to close modal
  useEffect(() => {
    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        closeModal();
      }
    };

    if (selectedItem) {
      window.addEventListener("keydown", handleEscapeKey);
      return () => window.removeEventListener("keydown", handleEscapeKey);
    }
  }, [selectedItem]);

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
        {galleryItems.map((item) => (
          <div key={item.id}>
            <div
              className="gallery-item cursor-pointer relative group overflow-hidden rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300"
              onClick={() => openModal(item)}
              onKeyDown={(e) => handleKeyPress(e, item)}
              role="button"
              tabIndex={0}
            >
              <Image
                src={item.thumbImage}
                alt={item.alt}
                title={item.alt}
                width={477}
                height={289}
                className="w-full h-auto transition-transform duration-300 group-hover:scale-105"
                loading="lazy"
              />
              <div className="gallery-item-details absolute inset-0 bg-black/80 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                <Image
                  src={item.logoImage}
                  alt={item.logoAlt}
                  title={item.logoAlt}
                  width={150}
                  height={50}
                  className="w-auto h-auto max-h-16"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Modal */}
      {selectedItem && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
          onClick={closeModal}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.code === "Space") {
              closeModal();
            }
          }}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto relative"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            role="document"
          >
            <button
              type="button"
              onClick={closeModal}
              className="absolute top-4 right-4 z-10 bg-white rounded-full p-2 hover:bg-gray-100 transition-colors"
              aria-label="Close"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>

            <div className="p-6 md:p-8">
              <Image
                src={selectedItem.fullImage}
                alt={selectedItem.alt}
                title={selectedItem.alt}
                width={950}
                height={608}
                className="w-full h-auto rounded-lg mb-6"
                loading="lazy"
              />
              
              <div className="mb-6 flex justify-center">
                <Image
                  src={selectedItem.modalLogoImage || selectedItem.logoImage}
                  alt={selectedItem.logoAlt}
                  title={selectedItem.logoAlt}
                  width={150}
                  height={50}
                  className="h-12 w-auto"
                  loading="lazy"
                />
              </div>

              {selectedItem.description && (
                <p className="text-gray-700 text-lg mb-6 text-center">{selectedItem.description}</p>
              )}

              {selectedItem.caseStudyLink && (
                <div className="flex justify-center">
                  <Link
                    href={selectedItem.caseStudyLink}
                    className="inline-block bg-primary text-white px-6 py-3 rounded-lg hover:bg-primary/90 transition-colors"
                  >
                    Read Case Study
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default DigitalSignageGalleryGrid;

